import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Moment } from 'moment';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { Case, CaseAttachment, CaseDocument, CaseScore, CaseStatus, CaseType } from '../models/case';
import { environment } from 'src/environments/environment';
import { Paginator } from '../models/paginator';
import { Approval, Document } from '../models/document';
import { EvidenceRecord, EvidenceRecordGroupByType } from '../models/evidenceRecord';
import { UploadAttachment } from '../models/uploadFile';
import { Data } from './document.service';
import { User } from '../models/user';
import moment from 'moment';
import { Trademark } from '../models/trademark';
import { TrademarkFamily } from '../models/trademarkFamily';
import { ProductService } from '../models/productService';
import { Note } from '../models/note';
import { Tag } from '../models/tag';

export interface CaseCreationOrUpdate {
  trademark: string;
  type: CaseType;
  lang: string;
  productsServices?: {
    ref: string,
    label: string,
    classNumber: number,
    trademark: string
  }[];
  niceClassification: number[];
  timePeriod: {
    start: string,
    end: string
  };
  propertyOffice?: string;
  identifier?: string;
  state: string;
}

export interface CasesByPhase {
  count: number;
  _id: { name: CaseStatus }
}


@Injectable({
  providedIn: 'root'
})
export class CaseService {

  constructor(
    private http: HttpClient
  ) { }


  create(caseToCreate: CaseCreationOrUpdate): Promise<Case> {
    return firstValueFrom(this.http
      .post<Case>(`${environment.backend}/cases`, caseToCreate));
  }


  retrieve(id: string): Promise<Case> {
    return firstValueFrom(this.http
      .get<Case>(`${environment.backend}/cases/${id}`));
  }

  retrieveElligibleDocuments(id: string, pageNumber: number, limit: number, offset: number, sort?: string): Promise<Paginator<CaseDocument>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)

    if (sort) {
      params = params.set('sort', sort);
    }

    return firstValueFrom(this.http
      .get<Paginator<CaseDocument>>(`${environment.backend}/cases/${id}/documents/elligible`, { params }));
  }

  retrieveDocuments(
    id: string,
    pageNumber: number,
    limit: number,
    offset: number,
    sort?: string,
    territories?: string[],
    classNumbers?: number[],
    productsServices?: ProductService[],
    trademarks?: Trademark[],
    trademarkFamilies?: TrademarkFamily[],
    classes?: string[],
    period?: { start?: Moment | number, end?: Moment | number },
    nonUse?: boolean,
    approval?: Approval[],
    query?: string,
    tags?: Tag[],
    isConfidential?: boolean
  ): Promise<Paginator<CaseDocument>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)

    if (sort) {
      params = params.set('sort', sort);
    }

    if (territories) {
      territories.forEach(territory => {
        params = params.append('countryOfDesignation[]', territory.toUpperCase());
      })
    }

    if (classNumbers) {
      classNumbers.forEach(classNumber => {
        params = params.append('classNumber[]', classNumber);
      })
    }

    if (productsServices) {
      productsServices.forEach(ps => {
        if (ps._id) {
          params = params.append('productService[]', ps._id);
        }
      })
    }

    if (classes) {
      classes.forEach((documentClass) => {
        params = params.append('documentClass[]', documentClass);
      })
    }

    if (trademarks) {
      trademarks.forEach((trademark) => {
        if (trademark._id) {
          params = params.append('trademark[]', trademark._id);
        }
      })
    }

    if (trademarkFamilies) {
      trademarkFamilies.forEach((family) => {
        if (family._id) {
          params = params.append('trademarkFamily[]', family._id);
        }
      })
    }

    if (approval) {
      approval.forEach((appr) => {
        params = params.append('approval[]', appr);
      })
    }

    if (period?.start) {
      const value = typeof period.start === 'number'
        ? period.start
        : period.start.toISOString(true);
      params = params.append('start', `${value}`);
    }

    if (period?.end) {
      const value = typeof period.end === 'number'
        ? period.end
        : period.end.toISOString(true);
      params = params.append('end', `${value}`);
    }

    if (nonUse !== undefined) {
      params = params.set('nonUse', nonUse);
    }

    if (tags) {
      tags.forEach((tag) => {
        if (tag._id) {
          params = params.append('tag[]', tag._id);
        }
      })
    }

    if (isConfidential !== undefined) {
      params = params.set('confidential', isConfidential);
    }

    if (query) {
      params = params.set('q', query);
    }

    return firstValueFrom(this.http
      .get<Paginator<CaseDocument>>(`${environment.backend}/cases/${id}/documents/search`, { params }));
  }

  aggregateDocuments(id: string, facet: string): Promise<Data[]> {
    return firstValueFrom(this.http
      .get<Data[]>(`${environment.backend}/cases/${id}/documents/aggregate/${facet}`));
  }

  link(caseId: string, documents: string[]): Promise<Case> {
    return firstValueFrom(this.http
      .put<Case>(`${environment.backend}/cases/${caseId}/link`, { documents }));
  }

  unlink(caseId: string, documents: string[]): Promise<Case> {
    return firstValueFrom(this.http
      .put<Case>(`${environment.backend}/cases/${caseId}/unlink`, { documents }));
  }

  countAll(): Promise<{ count: number }> {
    return firstValueFrom(this.http
      .get<{ count: number }>(`${environment.backend}/cases/count`));
  }

  graphAll(): Promise<CasesByPhase[]> {
    return firstValueFrom(this.http
      .get<CasesByPhase[]>(`${environment.backend}/cases/graph?date=${moment().format("YYYY-MM-DD")}`));
  }

  retrieveAll(pageNumber: number, limit: number, offset: number, sort: string, trademarks?: Trademark[], date?: Moment, query?: string): Promise<Paginator<Case>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)
      .set('sort', sort);


    if (trademarks) {
      trademarks.forEach((trademark) => {
        if (trademark._id)
          params = params.append('trademark[]', trademark._id);
      })
    }

    if (date) {
      params = params.append('date', date.format("YYYY-MM-DD"))
    }

    if (query) {
      params = params.set('q', query);
    }

    return firstValueFrom(this.http
      .get<Paginator<Case>>(`${environment.backend}/cases/search`, { params }));
  }

  update(id: string, updatedCase: CaseCreationOrUpdate): Promise<Case> {
    return firstValueFrom(this.http
      .put<Case>(`${environment.backend}/cases/${id}`, updatedCase));
  }

  updateCaseState(id: string, state: string): Promise<Case> {
    return firstValueFrom(this.http.put<Case>(`${environment.backend}/cases/${id}/state/${state}`, {}))
  }

  addNote(id: string, comment: string): Promise<Note> {
    return firstValueFrom(this.http
      .post<Note>(`${environment.backend}/cases/${id}/notes`, { comment }));
  }

  deleteNote(id: string, noteId: string): Promise<void> {
    return firstValueFrom(this.http
      .delete<void>(`${environment.backend}/cases/${id}/notes/${noteId}`));
  }

  delete(id: string): Promise<any> {
    return firstValueFrom(this.http
      .delete<any>(`${environment.backend}/cases/${id} `));
  }

  retrieveEvidenceRecords(id: string, pageNumber: number, limit: number, offset: number, sort: string): Promise<Paginator<EvidenceRecord>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)
      .set('sort', sort);


    return firstValueFrom(this.http
      .get<Paginator<EvidenceRecord>>(`${environment.backend}/cases/${id}/evidence`, { params }));
  }

  retrieveEvidenceRecord(id: string, evidenceRecordId: string) {
    return firstValueFrom(this.http
      .get<EvidenceRecord>(`${environment.backend}/cases/${id}/evidence/${evidenceRecordId}`, {}));
  }

  /**
   * Generate an evidence record for a case.
   * @param id Case ID
   * @param groupBy list of groupBy elements
   * @param specificDocumentIds Optional array of specific document IDs to include in the evidence record. If not provided, all documents linked to the case will be included.
   * @param includeDocumentDataPage Whether to generate and append the document data page to the end of annotated document (when applicable: crawled docs only)
   * @param annotateDocuments Whether to annotate and redact evidences
   * @param includePendingDocuments Whether to include the documents pending approval (the backend's default is
   * to include them). Rejected documents are never included.
   */
  generateEvidenceRecord(
    id: string,
    groupBy: EvidenceRecordGroupByType[] = [],
    specificDocumentIds: string[] = [],
    includeDocumentDataPage: boolean = false,
    annotateDocuments: boolean = false,
    includePendingDocuments: boolean = true,
  ) {
    return firstValueFrom(
      this.http.post<EvidenceRecord>(
        `${environment.backend}/cases/${id}/evidence/generate`,
        {
          groupBy,
          specificDocumentIds,
          includeDocumentDataPage,
          annotateDocuments,
          includePendingDocuments,
        },
      ),
    );
  }

  downloadEvidenceRecord(id: string, evidenceRecordId: string) {
    return firstValueFrom(this.http
      .get(`${environment.backend}/cases/${id}/evidence/${evidenceRecordId}/download`, {
        responseType: 'blob'
      }));
  }

  deleteEvidenceRecord(id: string, evidenceRecordId: string) {
    return firstValueFrom(this.http
      .delete<void>(`${environment.backend}/cases/${id}/evidence/${evidenceRecordId}`));
  }

  createAttachment(id: string, file: UploadAttachment) {
    const formData = new FormData();
    formData.append("attachment", file);
    formData.append("name", file.name);
    formData.append("appendix", file.appendix ? 'true' : 'false');
    formData.append("nonUse", file.nonUse ? 'true' : 'false');
    if (file.description)
      formData.append("description", file.description);

    return lastValueFrom(this.http.post<CaseAttachment>(`${environment.backend}/cases/${id}/attachments`, formData));
  }

  createNonUseAttachment(id: string, file: UploadAttachment) {
    const attachment = {
      name: file.name,
      description: file.description,
      appendix: true,
      document: file.document,
      nonUse: true
    };

    return lastValueFrom(this.http.post<CaseAttachment>(`${environment.backend}/cases/${id}/attachments/non-use`, attachment));
  }

  updateAttachment(id: string, attachmentId: string, attachment: UploadAttachment) {
    return lastValueFrom(this.http.put<CaseAttachment>(`${environment.backend}/cases/${id}/attachments/${attachmentId}`, attachment));
  }

  countAttachments(id: string) {
    return lastValueFrom(this.http.get<number>(`${environment.backend}/cases/${id}/attachments/count`));
  }

  retrieveAttachments(id: string) {
    return lastValueFrom(this.http.get<CaseAttachment[]>(`${environment.backend}/cases/${id}/attachments`));
  }

  downloadAttachment(id: string, attachmentId: string) {
    return firstValueFrom(this.http
      .get(`${environment.backend}/cases/${id}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      }));
  }

  deleteAttachment(id: string, attachmentId: string) {
    return firstValueFrom(this.http
      .delete(`${environment.backend}/cases/${id}/attachments/${attachmentId}`, {
        responseType: 'blob'
      }));
  }

  retrieveUsers(id: string, pageNumber: number, limit: number, offset: number, sort: string): Promise<Paginator<User>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)
      .set('sort', sort);

    return firstValueFrom(this.http
      .get<Paginator<User>>(`${environment.backend}/cases/${id}/users`, { params }));
  }

  getScoreForClass(id: string, classNumber: number) {
    return lastValueFrom(this.http.get<CaseScore>(`${environment.backend}/cases/${id}/score/class/${classNumber}`));
  }

  getRelevantTerritories(id: string) {
    return lastValueFrom(this.http.get<string[]>(`${environment.backend}/cases/${id}/territories`));
  }
}
